class HeaderMenu extends HTMLElement {
  constructor() {
    super();

    this.background = this.querySelector('.header__menu__background');
    this.mobileDrawer = this.querySelector('.header__mobile-drawer');
    this.mobileToggle = this.closest('.header')?.querySelector('[data-mobile-menu-toggle]');
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    this.mobileToggle?.addEventListener('click', this.handleMobileToggleClick);
    this.background.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.mobileToggle?.removeEventListener('click', this.handleMobileToggleClick);
    this.background.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleClick = (event) => {
    const closeButton = event.target.closest('[data-mobile-close]');
    if (closeButton) {
      this.closeMobileDrawer({ focusToggle: true });
      return;
    }

    const forwardButton = event.target.closest('[data-mobile-forward]');
    if (forwardButton) {
      this.showMobilePanel(forwardButton.getAttribute('aria-controls'));
      return;
    }

    const backButton = event.target.closest('[data-mobile-back]');
    if (backButton) {
      this.showMobilePanel(backButton.getAttribute('aria-controls'));
      return;
    }

    const columnLink = event.target.closest('.header__submenu-link');
    if (columnLink) {
      const isExpanded = columnLink.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        this.closeColumn(columnLink);
      } else {
        this.openColumn(columnLink);
      }
      return;
    }

    const toggle = event.target.closest('.header__submenu-toggle');
    if (!toggle || toggle.hasAttribute('data-search-toggle')) return;

    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    this.closeSubmenus();

    if (!isOpen) {
      this.closest('.header')?.closeSearchDrawer?.();
      this.openSubmenu(toggle);
    }
  };

  handleMobileToggleClick = () => {
    const isOpen = this.mobileToggle.getAttribute('aria-expanded') === 'true';
    this.closeSubmenus();

    if (isOpen) {
      this.closeMobileDrawer();
    } else {
      this.closest('.header')?.closeSearchDrawer?.();
      this.openMobileDrawer();
    }
  };

  handleOutsideClick = () => {
    this.closeSubmenus();
    this.closeMobileDrawer();
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.closeSubmenus({ focusToggle: true });
      this.closeMobileDrawer({ focusToggle: true });
    }
  };

  openMobileDrawer() {
    if (!this.mobileDrawer || !this.mobileToggle) return;

    this.resetMobilePanels();
    this.mobileToggle.setAttribute('aria-expanded', 'true');
    this.mobileDrawer.classList.add('is-open');
    this.classList.add('is-open');
    document.documentElement.classList.add('scroll-locked');
    this.closest('.header')?.classList.remove('header--hidden');
  }

  closeMobileDrawer({ focusToggle = false } = {}) {
    if (!this.mobileDrawer || !this.mobileToggle) return;
    if (this.mobileToggle.getAttribute('aria-expanded') !== 'true') return;

    this.mobileToggle.setAttribute('aria-expanded', 'false');
    this.mobileDrawer.classList.remove('is-open');
    this.classList.remove('is-open');
    document.documentElement.classList.remove('scroll-locked');

    if (focusToggle) {
      this.mobileToggle.focus();
    }
  }

  resetMobilePanels() {
    this.mobileDrawer.querySelectorAll('.header__mobile-panel').forEach((panel) => {
      panel.hidden = !('root' in panel.dataset);
    });
  }

  showMobilePanel(id) {
    if (!id || !this.mobileDrawer) return;

    const target = this.mobileDrawer.querySelector(`#${CSS.escape(id)}`);
    if (!target) return;

    this.mobileDrawer.querySelectorAll('.header__mobile-panel').forEach((panel) => {
      panel.hidden = panel !== target;
    });
    this.mobileDrawer.scrollTop = 0;
  }

  openSubmenu(toggle) {
    toggle.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    document.documentElement.classList.add('scroll-locked');
    this.closest('.header')?.classList.remove('header--hidden');
  }

  openColumn(link) {
    const list = link.closest('.header__submenu-list');
    const panels = list.closest('.header__menu-column').parentElement.querySelectorAll(
      ':scope .header__menu-panel'
    );

    list.querySelectorAll('.header__submenu-link').forEach((item) => {
      item.setAttribute('aria-expanded', item === link ? 'true' : 'false');
    });

    panels.forEach((panel) => {
      panel.hidden = panel.id !== link.getAttribute('aria-controls');
    });
  }

  closeColumn(link) {
    const list = link.closest('.header__submenu-list');
    const panels = list.closest('.header__menu-column').parentElement.querySelectorAll(
      ':scope .header__menu-panel'
    );

    link.setAttribute('aria-expanded', 'false');

    panels.forEach((panel) => {
      if (panel.id === link.getAttribute('aria-controls')) {
        panel.hidden = true;
      }
    });
  }

  closeSubmenus({ focusToggle = false } = {}) {
    const openToggle = this.querySelector('.header__submenu-toggle[aria-expanded="true"]');
    if (!openToggle) return;

    openToggle.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    document.documentElement.classList.remove('scroll-locked');

    if (focusToggle) {
      openToggle.focus();
    }
  }
}

customElements.define('header-menu', HeaderMenu);
